import { Box, CircularProgress, Drawer, FormLabel, MenuItem, Select } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { CustomJoyrideTour } from 'components/facets/CustomJoyrideTour'
import { CustomTooltip } from 'components/facets/CustomTooltip'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import { TableSearchNavigator } from 'components/facets/TableSearchNavigator'
import { useUser } from 'components/providers/UserProvider'
import sellerImg from 'img/seller-2.png'
import { deletePurchaseEvent, editUserAttribute } from 'lib/api'
import { useAppContext } from 'lib/hooks'
import { useFetchPurchaseEvents } from 'lib/hooks/useFetchPurchaseEvents'
import { usePurchaseEventsTour } from 'lib/hooks/usePurchaseEventsTour'
import { useTaxationStore } from 'lib/hooks/useTaxationStore'
import { getPriceFormat } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { AutoSizer, Column, Table, TableRowRenderer } from 'react-virtualized'
import { getDateYYYYMMDD, PurchaseEvent } from 'shared'
import { ExportPurchaseArticles } from './ExportPurchaseArticles'
import { PurchaseEventCreation } from './PurchaseEventCreation'
import { PurchaseEventDetails } from './PurchaseEventDetails'

export const PurchaseEventPresenter = (): JSX.Element => {
  const { user, setUser, fetchUser } = useUser()
  const { t } = useTranslation()
  const history = useHistory()
  const { isMobile } = useAppContext()

  const enableMarginTaxation = async () => {
    await editUserAttribute(true, 'enableMarginTaxation')
    await fetchUser()
    history.push('/account?tab=taxation&marginEnabled=true')
  }

  const [openMarginDialog, setOpenMarginDialog] = useState(false)
  const { loading, fetchPurchaseEvents, purchaseEvents: peStore } = useFetchPurchaseEvents()
  const [purchaseEvents, setPurchaseEvents] = useState<PurchaseEvent[]>([])
  const [filteredPurchaseEvents, setFilteredPurchaseEvents] =
    useState<PurchaseEvent[]>(purchaseEvents)
  const [filter, setFilter] = useState<
    'View all' | 'Only created by user' | 'Created automatically'
  >('View all')
  const [openPurchaseEventDialog, setOpenPurchaseEventDialog] = useState(false)
  const tableRef = useRef<any>(null)
  const { enqueueSnackbar } = useSnackbar()

  const [showLotInfo, setShowLotInfo] = useState(false)
  const [currentPurchaseEvent, setCurrentPurchaseEvent] = useState<PurchaseEvent | undefined>(
    undefined
  )

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLastPage, setIsLastPage] = useState<boolean>(false)

  const [searchText, setSearchText] = useState('')
  const [matchIndices, setMatchIndices] = useState<number[]>([])
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  setMatchIndices

  const [hasPurchases, setHasPurchases] = useState(false)
  const [view, setView] = useState<'home' | 'purchases'>('home')
  const [viewTransitioning, setViewTransitioning] = useState(false)
  const [run, setRun] = useState(false)
  const [startingIndexTour] = useTaxationStore((s) => [s.startingIndexTour])
  const [stepIndex, setStepIndex] = useState(0)
  const { steps, runTour } = usePurchaseEventsTour({
    user,
    setUser,
    run,
    setRun,
    stepIndex,
    setStepIndex,
    loading,
    peStore,
    hasPurchases,
    view,
  })
  stepIndex

  /*
   * This effect acts as a "bootstrap / gate" for Joyride when the page mounts (or remounts),
   * especially after the user navigates away to add purchases and then comes back.
   *
   * Why it exists:
   * - If we are resuming (startingIndexTour > 0), we must prevent Joyride from starting immediately at stepIndex=0.
   *   Otherwise Joyride mounts on the default step while the UI (drawer) is still closed, which can cause:
   *   - flashes / jumps (Joyride computes targets for the wrong step)
   *   - orphan beacons / inconsistent internal state when targets are not in the DOM yet
   *
   * What this effect does:
   * - When startingIndexTour > 0: it keeps Joyride OFF (run=false) so the hook can perform the real resume sequence:
   *   open the drawer (automated click + waits) -> hard reset -> restart Joyride at the desired step.
   * - When not resuming: it initializes stepIndex and run based on user settings.
   *
   * Mental model:
   * - Parent controls "when Joyride is allowed to mount" (gate).
   * - The tour hook controls "how to resume / advance once mounted" (state machine).
   */
  const bootstrapRef = useRef(false)

  useEffect(() => {
    if (loading) return

    // If we are resuming (startingIndexTour > 0), do NOT start Joyride here.
    // The hook will handle: open drawer -> setStepIndex -> setRun(true).
    if (startingIndexTour && startingIndexTour > 0) {
      bootstrapRef.current = true
      setRun(false)
      return
    }

    // Only initialize on first mount, not when startingIndexTour goes back to 0 after a resume
    if (bootstrapRef.current) return

    bootstrapRef.current = true
    setStepIndex(0)
    setRun(user.enabledNotifications?.displayPurchaseEventsTour ?? false)
  }, [loading, startingIndexTour, user.enabledNotifications?.displayPurchaseEventsTour])

  const handleInspectPurchaseEvent = (event: PurchaseEvent) => {
    setCurrentPurchaseEvent(event)
    setShowLotInfo(true)
  }

  const fetchPe = async (page: number) => {
    const { events, nextPage } = await fetchPurchaseEvents(page)
    setPurchaseEvents(events)
    setIsLastPage(!nextPage)
  }

  useEffect(() => {
    fetchPe(1)
  }, [])

  const handleClose = () => {
    setShowLotInfo(false)
  }

  useEffect(() => {
    if (!searchText.trim()) {
      setMatchIndices([])
      setCurrentMatchIndex(0)
      return
    }

    const lowerSearch = searchText.toLowerCase()

    const searchTarget = filteredPurchaseEvents

    const matches = searchTarget.reduce<number[]>((acc, event, index) => {
      const matchInName =
        event.seller?.name?.toLowerCase().includes(lowerSearch) ||
        event.seller?.vatNumber?.toLowerCase().includes(lowerSearch) ||
        event.lotNumber?.toLowerCase().includes(lowerSearch)

      if (matchInName) acc.push(index)
      return acc
    }, [])

    setMatchIndices(matches)
    setCurrentMatchIndex(0)
  }, [searchText, filteredPurchaseEvents])

  // Scroll to current match
  useEffect(() => {
    if (matchIndices.length > 0 && tableRef.current) {
      tableRef.current.scrollToRow(matchIndices[currentMatchIndex])
    }
  }, [matchIndices, currentMatchIndex])

  const handleDelete = async (purchaseEventId: number) => {
    try {
      const response = await deletePurchaseEvent(Number(purchaseEventId))
      if (response.success) {
        setCurrentPage(1)
        fetchPe(1)
        enqueueSnackbar(t('taxation.success.purchaseEventDeleted'), { variant: 'success' })
      }
    } catch (error) {
      enqueueSnackbar(t('taxation.errors.errorDeletingPe'), { variant: 'error' })
    }
  }

  // useEffect(() => {
  //   const displayTour = user.enabledNotifications.displayPurchaseEventsTour
  //   if (!loading) setRun(displayTour)
  // }, [loading])

  useEffect(() => {
    if (filter === 'View all') setFilteredPurchaseEvents(purchaseEvents)
    else if (filter === 'Only created by user')
      setFilteredPurchaseEvents(
        purchaseEvents.filter((event) => !event.lotNumber?.startsWith('Unassigned-'))
      )
    else if (filter === 'Created automatically')
      setFilteredPurchaseEvents(
        purchaseEvents.filter((event) => event.lotNumber?.startsWith('Unassigned-'))
      )
  }, [filter, purchaseEvents, peStore.length])

  const rowRenderer: TableRowRenderer = ({ index, key, style, columns, className, rowData }) => {
    const isHighlighted = matchIndices.includes(index)
    rowData

    return (
      <div
        key={key}
        style={style}
        className={`${className} ${isHighlighted ? 'highlight-row' : ''}`}
      >
        {columns}
      </div>
    )
  }

  return (
    <div className="taxation-root">
      <CustomJoyrideTour
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        callback={runTour}
        continuous
        showSkipButton
        spotlightClicks
        disableOverlayClose
        disableScrolling
        scrollToFirstStep={false}
        disableScrollParentFix
      />
      <div className="h-100">
        {loading ? (
          <div className="w-100 d-flex flex-column align-items-center loading-progress">
            <h6 className="pb-5">{t('common.loading')}</h6>
            <CircularProgress size={100} />
          </div>
        ) : view === 'home' ? (
          <div className="w-100 d-flex flex-column align-items-center mt-2">
            <img src={sellerImg} style={{ maxWidth: 500, width: '100%', height: 'auto' }} />
            <Box maxWidth={700} width="100%" px={2}>
              <h2 className="fst-italic mt-3 text-center">{t('taxation.home.intro')}</h2>
            </Box>

            <div className="taxation-action-cards">
              <div className="taxation-action-card" data-testid="tax-export-box">
                <div>
                  <h4 className="primary-color fw-medium">{t('taxation.home.exportTitle')}</h4>
                  <p>{t('taxation.home.exportDesc')}</p>
                </div>
                <Button
                  variant="contained"
                  onClick={() => history.push('/exports?tab=taxation')}
                  data-testid="tax-export-btn"
                >
                  {t('taxation.home.exportBtn')}
                </Button>
              </div>
              <div className="taxation-action-card" data-testid="tax-purchases-box">
                <div>
                  <h4 className="primary-color fw-medium">{t('taxation.home.purchasesTitle')}</h4>
                  <p>{t('taxation.home.purchasesDesc')}</p>
                </div>
                {purchaseEvents.length === 0 ? (
                  <Button
                    variant="contained"
                    onClick={() => setOpenPurchaseEventDialog(true)}
                    data-testid="manage-pe"
                  >
                    {t('taxation.home.createPurchaseBtn')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => {
                      setViewTransitioning(true)
                      setView('purchases')
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => setViewTransitioning(false))
                      })
                    }}
                    data-testid="manage-pe"
                  >
                    {t('taxation.home.managePurchasesBtn')}
                  </Button>
                )}
              </div>
              <div className="taxation-action-card" data-testid="tax-margin-box">
                <div className="mb-3">
                  <h4 className="primary-color fw-medium">{t('taxation.home.marginTitle')}</h4>
                  <p>{t('taxation.home.marginDesc')}</p>
                  {!user.enableMarginTaxation && (
                    <div
                      className="alert alert-warning mt-2 mb-0 p-2"
                      style={{ fontSize: '0.8rem' }}
                    >
                      <div className="d-flex flex-row">
                        <i className="fa-light fa-triangle-exclamation me-2 fs-6" />
                        <p className="p-0 m-0">
                          <strong>{t('taxation.settings.important')}:</strong>{' '}
                          {t('taxation.settings.importantNote')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                {user.enableMarginTaxation && (
                  <span className="mb-2 fst-italic">{t('taxation.home.marginEnabled')}</span>
                )}
                {user.enableMarginTaxation ? (
                  <Button
                    variant="contained"
                    data-testid="tax-margin-settings"
                    onClick={() => history.push('/account?tab=taxation')}
                  >
                    {t('taxation.home.goToSettings')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    data-testid="tax-margin-enable-btn"
                    onClick={() => setOpenMarginDialog(true)}
                  >
                    {t('taxation.home.marginEnableBtn')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : viewTransitioning ? (
          <div className="w-100 d-flex flex-column align-items-center loading-progress">
            <h6 className="pb-5">{t('common.loading')}</h6>
            <CircularProgress size={100} />
          </div>
        ) : (
          <div className="pb-5">
            <h2 className="mb-4">{t('taxation.purchaseEvents')}</h2>
            <div className="w-100">
              <Button
                variant="contained"
                onClick={() => setOpenPurchaseEventDialog(true)}
                data-testid="create-pe"
                className="add-pe-btn"
              >
                <>
                  <i className="fa-solid fa-folder-plus me-2"></i>
                  <span>{t('taxation.addPurchaseEvent')}</span>
                </>
              </Button>
            </div>
            <div
              className={`mt-5 d-flex ${
                isMobile
                  ? 'flex-column-reverse align-items-start'
                  : 'flex-row flex-nowrap justify-content-between align-items-end'
              }`}
              style={{ gap: '1rem' }}
            >
              <div className="d-flex align-items-center gap-2">
                <TableSearchNavigator
                  searchText={searchText}
                  setSearchText={setSearchText}
                  placeholderText={t('taxation.searchLot')}
                  matchIndices={matchIndices}
                  currentMatchIndex={currentMatchIndex}
                  setCurrentMatchIndex={setCurrentMatchIndex}
                  maxWidth="400px"
                />
              </div>
              <div className="d-flex flex-row align-items-end flex-shrink-0">
                <FormLabel className="me-3 mb-1 text-end" style={{ whiteSpace: 'nowrap' }}>
                  {t('taxation.filterPurchaseEvents')}:
                </FormLabel>
                <Box width={300}>
                  <Select
                    value={filter}
                    onChange={(e) => {
                      setFilter(
                        e.target.value as
                          | 'View all'
                          | 'Only created by user'
                          | 'Created automatically'
                      )
                    }}
                    defaultValue="Select"
                    type="select"
                    fullWidth
                    name="purchase-event"
                    data-testid="filter-pe"
                  >
                    <MenuItem key={'all'} value={'View all'}>
                      {t('taxation.viewAll')}
                    </MenuItem>

                    <MenuItem key={'manual'} value={'Only created by user'}>
                      {t('taxation.onlyCreatedByUser')}
                    </MenuItem>
                    <MenuItem key={'automatical'} value={'Created automatically'}>
                      {t('taxation.createdAutomatically')}
                    </MenuItem>
                  </Select>
                </Box>
              </div>
            </div>

            <div className="mt-3">
              <AutoSizer disableHeight>
                {({ width }) => (
                  <Table
                    ref={tableRef}
                    width={width}
                    height={41 * 50}
                    headerHeight={40}
                    rowHeight={40}
                    rowCount={filteredPurchaseEvents.length}
                    rowGetter={({ index }) => filteredPurchaseEvents[index]}
                    rowRenderer={rowRenderer}
                    overscanRowCount={5}
                  >
                    <Column
                      width={45}
                      flexGrow={0}
                      flexShrink={0}
                      label=""
                      dataKey="inspect"
                      cellRenderer={({ rowData: a, rowIndex }) => (
                        <div
                          className="d-flex align-items-left"
                          onClick={() => handleInspectPurchaseEvent(a)}
                        >
                          <i
                            data-testid={'inspect-pe-' + rowIndex}
                            className="fa-regular fa-magnifying-glass table-action-icon"
                          ></i>
                        </div>
                      )}
                    />

                    <Column
                      width={120}
                      flexGrow={1}
                      flexShrink={0}
                      label={t('taxation.lotInformation.purchaseDate')}
                      dataKey="purchase-date"
                      cellRenderer={({ rowData: a }) => (
                        <span>{getDateYYYYMMDD(new Date(a.purchaseDate))}</span>
                      )}
                    />

                    <Column
                      width={120}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('taxation.lotInformation.lotNumber')}
                      dataKey="lotNumber"
                      cellRenderer={({ rowData: a }) => <span>{a.lotNumber}</span>}
                    />

                    <Column
                      width={200}
                      flexGrow={2}
                      flexShrink={1}
                      label={t('taxation.seller.seller')}
                      dataKey="seller"
                      cellRenderer={({ rowData: a }) => <span>{a.seller?.name}</span>}
                    />

                    <Column
                      width={120}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('taxation.lotInformation.totalQuantity')}
                      dataKey="totalQuantity"
                      cellRenderer={({ rowData: a, rowIndex }) => (
                        <span data-testid={`linked-qty-${rowIndex}`}>
                          {a.quantity ?? '-'}
                          {a.quantity != null && a.totalRegisteredQuantity !== a.quantity && (
                            <CustomTooltip
                              title={
                                t('taxation.lotInformation.linkedQuantityTooltip', {
                                  quantity: a.totalRegisteredQuantity,
                                }) as string
                              }
                            >
                              <i className="fa-regular fa-triangle-exclamation error-color fs-6 ms-1"></i>
                            </CustomTooltip>
                          )}
                        </span>
                      )}
                    />

                    <Column
                      width={120}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('taxation.lotInformation.totalPrice')}
                      dataKey="totalPrice"
                      cellRenderer={({ rowData: a, rowIndex }) => (
                        <span data-testid={`linked-price-${rowIndex}`}>
                          {a.price != null ? getPriceFormat(a.price, a.currency) : '-'}
                          {a.price != null &&
                            a.totalRegisteredPrice?.toFixed(2) !== a.price.toFixed(2) && (
                              <CustomTooltip
                                title={
                                  t('taxation.lotInformation.linkedPriceTooltip', {
                                    price: getPriceFormat(a.totalRegisteredPrice, a.currency),
                                  }) as string
                                }
                              >
                                <i className="fa-regular fa-triangle-exclamation error-color fs-6 ms-1"></i>
                              </CustomTooltip>
                            )}
                        </span>
                      )}
                    />

                    <Column
                      width={90}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('taxation.lotInformation.withVat')}
                      dataKey="withVat"
                      headerClassName="centered-header"
                      cellRenderer={({ rowData: a }) => (
                        <Box textAlign={'center'}>
                          <span>{a.withVat ? t('common.yes') : t('common.no')}</span>
                        </Box>
                      )}
                    />

                    <Column
                      width={70}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('taxation.lotInformation.receipt')}
                      dataKey="receipt"
                      headerClassName="centered-header"
                      cellRenderer={({ rowData: a, rowIndex }) => (
                        <div
                          className="w-100 h-100 d-flex align-items-center justify-content-center"
                          data-testid={`receipt-${rowIndex}`}
                        >
                          {a.hasReceipt ? (
                            <>
                              <i className="fa-light fa-file-check table-icon"></i>
                            </>
                          ) : (
                            <span>—</span>
                          )}
                        </div>
                      )}
                    />

                    <Column
                      width={50}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('common.csv')}
                      dataKey="csv"
                      headerClassName="centered-header"
                      cellRenderer={({ rowData: a, rowIndex }) => (
                        <span data-testid={`csv-${rowIndex}`}>
                          <ExportPurchaseArticles purchaseEvent={a} />
                        </span>
                      )}
                    />

                    <Column
                      width={70}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('common.delete')}
                      dataKey="delete"
                      headerClassName="centered-header"
                      cellRenderer={({ rowData: a, rowIndex }) => (
                        <Box textAlign={'center'}>
                          <i
                            data-testid={`delete-event-${rowIndex}`}
                            className={`fa-light fa-trash table-action-icon`}
                            onClick={() => handleDelete(a.id)}
                          ></i>
                        </Box>
                      )}
                    />
                  </Table>
                )}
              </AutoSizer>
            </div>

            {(currentPage > 1 || !isLastPage) && (
              <TablePaginationControls
                currentPage={currentPage}
                setPage={(page) => {
                  setCurrentPage(page)
                  fetchPe(page)
                }}
                isLastPage={isLastPage}
                startFromZero={false}
              />
            )}

            <Drawer
              open={!!showLotInfo}
              anchor="right"
              onClose={handleClose}
              PaperProps={isMobile ? { style: { width: '100%' } } : undefined}
            >
              <PurchaseEventDetails
                lot={currentPurchaseEvent!}
                handleClose={handleClose}
                purchaseEvents={purchaseEvents}
                setPurchaseEvents={setPurchaseEvents}
                setHasPurchases={setHasPurchases}
              />
            </Drawer>
          </div>
        )}
      </div>
      {openPurchaseEventDialog && (
        <ClosableDialog
          fullWidth
          maxWidth="lg"
          open={openPurchaseEventDialog}
          onClose={() => setOpenPurchaseEventDialog(false)}
        >
          <PurchaseEventCreation
            onLotCreated={async () => {
              setOpenPurchaseEventDialog(false)
              setCurrentPage(1)
              await fetchPe(1)
              setView('purchases')
            }}
          />
        </ClosableDialog>
      )}
      <ClosableDialog
        fullWidth
        maxWidth="sm"
        open={openMarginDialog}
        onClose={() => setOpenMarginDialog(false)}
        hideButton
      >
        <div className="p-4">
          <h3>{t('taxation.settings.enableMarginSchemeTitle')}</h3>
          <p className="mt-3">{t('taxation.settings.enableMarginSchemeDescription')}</p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button variant="outlined" onClick={() => setOpenMarginDialog(false)}>
              {t('common.close')}
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                await enableMarginTaxation()
                setOpenMarginDialog(false)
              }}
            >
              {t('common.activate')}
            </Button>
          </div>
        </div>
      </ClosableDialog>
    </div>
  )
}
